import {
  type IGroupOption,
  type IOption,
  NumberInput,
  RemoveButton,
  Select,
  TextField,
} from "@components/ui-kit";
import { t } from "@lingui/macro";
import {
  fieldNumberModelToInputProps,
  fieldToInputProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import type { TPrepaymentSplitForm } from "../../../../form";
import { InvoiceTypeEnum, useInvoiceStore } from "../../../../store";

const useFilterSelectedCreditAccount = (args: {
  options: IOption[] | IGroupOption[];
  selectedValues: string[];
  includeId: string;
}) =>
  useMemo(() => {
    return args.options.map((i) => {
      if ("options" in i) {
        return {
          ...i,
          options: i.options.filter((j) => {
            if (j.value === args.includeId) return true;
            if (args.selectedValues.includes(j.value)) return false;

            return true;
          }),
        };
      }

      return i;
    });
  }, [args.selectedValues]) as IOption[] | IGroupOption[];

interface PrepaymentItemProps {
  item: TPrepaymentSplitForm;
  onRemove: () => void;
}

export const PrepaymentItem = observer(function PrepaymentItem(
  props: PrepaymentItemProps,
) {
  const { workspaceStore } = useStore();
  const store = useInvoiceStore();
  const { item, onRemove } = props;

  const usedCreditAccounts = store.form.prepayment_splits.$.map(
    (i) => i.$.brand_credit_account_id.value,
  );

  const creditAccountsOptions = useMemo(
    () =>
      store.type === InvoiceTypeEnum.CreditNote
        ? store.creditAccountsOptions
        : store.creditAccountsOptionsWithNew,
    [store.type],
  );

  const filteredCreditAccountsOptions = useFilterSelectedCreditAccount({
    options: creditAccountsOptions as IOption[] | IGroupOption[],
    selectedValues: usedCreditAccounts,
    includeId: item.$.brand_credit_account_id.value,
  });

  const isCreditAccountCreatedByThisInvoice = !!store.getOIPrepaymentSplitBy(
    item.$.brand_credit_account_id.value,
  );

  const onSelectCreditAccount = (val: string) => {
    item.$.brand_credit_account_id.onChange(val);

    if (val === "new" && store.selectedBrandsOptions.length === 1) {
      item.$.brand_id.onChange(store.selectedBrandsOptions[0].value);
    }
  };

  return (
    <div className="flex space-x-6">
      <div className="w-1/2 flex space-x-6">
        <div className="w-4/6">
          <Select
            placeholder={t`Choose credit account`}
            value={item.$.brand_credit_account_id.value}
            onChange={onSelectCreditAccount}
            options={filteredCreditAccountsOptions}
            isDisabled={
              isCreditAccountCreatedByThisInvoice || store.selectedMultipleTeams
            }
          />
        </div>
        <div className="w-2/6">
          <NumberInput
            currency
            placeholder={t`Amount`}
            suffix={workspaceStore.settings?.currency.code}
            disabled={store.selectedMultipleTeams}
            {...fieldNumberModelToInputProps(item.$.amount)}
          />
        </div>
      </div>
      <div className="flex w-1/2 items-start space-x-6">
        <div className="w-5/12">
          {item.$.brand_credit_account_id.value === "new" &&
            store.type !== InvoiceTypeEnum.CreditNote && (
              <TextField
                type="text"
                placeholder={t`Title of credit account`}
                {...fieldToInputProps(item.$.title)}
              />
            )}
        </div>
        <div className="w-5/12">
          {item.$.brand_credit_account_id.value === "new" && (
            <Select
              placeholder={t`Select brand`}
              {...fieldToSelectProps(
                item.$.brand_id,
                store.selectedBrandsOptions,
              )}
            />
          )}
        </div>
        {!isCreditAccountCreatedByThisInvoice && (
          <RemoveButton onClick={onRemove} />
        )}
      </div>
    </div>
  );
});
